import {
  P2GBox,
  P2GButton,
  P2GDialog,
  P2GGrid,
  P2GIcon,
  P2GLink,
  P2GRadio,
  P2GText,
  SvgClose,
  SvgFillWarningTriangle
} from '@parcel2go/core-ui';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import P2GOptionContainer from '~/components/core/option/option';

export type props = {
  onConfirm: (isSameContents: boolean) => void;
  onRevert: () => void;
  origin: string;
  destination: string;
  count: number;
  isSendToMultiple: boolean;
};

export default function SendToMultiple({ onConfirm, count, isSendToMultiple, onRevert, origin, destination }: props) {
  const [open, setOpen] = useState(false);
  const [isSameContents, setIsSameContents] = useState<boolean | undefined>(undefined);
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const { track } = useAnalytics();

  const payload = { count, origin, destination };

  const onClick = () => {
    track({ type: 'send_to_multiple_clicked', payload: { ...payload } });
    setOpen(true);
    setIsSameContents(undefined);
  };

  const revert = () => {
    track({ type: 'send_to_multiple_reverted', payload: { ...payload } });
    onRevert();
  };

  const onYesClick = () => {
    track({
      type: 'send_to_multiple_yes',
      payload: { ...payload, isSameContents: !!isSameContents }
    });
    onConfirm(!!isSameContents);
    setOpen(false);
  };

  const onNoClick = () => {
    track({ type: 'send_to_multiple_no', payload: { ...payload } });
    setOpen(false);
  };

  return (
    <>
      <P2GDialog
        open={open}
        onClose={onNoClick}
        closeButtonText="Cancel"
        title=""
        desktopViewOverride="modal"
        content={
          <>
            <P2GBox>
              <P2GBox
                pt={1}
                pb={1}
                display="flex"
                justifyContent="end"
                alignItems="center"
                onClick={onNoClick}
                gap={1}
                style={{ cursor: 'pointer' }}
              >
                <P2GText variant="body1" fontWeight="bold">
                  Close
                </P2GText>
                <P2GIcon size="sm">
                  <SvgClose />
                </P2GIcon>
              </P2GBox>
            </P2GBox>
            <P2GGrid container wrap="nowrap" gap={2}>
              <P2GGrid item flexShrink={1}>
                <P2GIcon palette="error" size="sm">
                  <SvgFillWarningTriangle />
                </P2GIcon>
              </P2GGrid>
              <P2GGrid item flexGrow={1}>
                <P2GText variant="h4">
                  {t('SEND_TO_MULTIPLE_TITLE', { defaultValue: 'Sending to Multiple Addresses' })}
                </P2GText>
                <P2GBox mt={2}>
                  <P2GBox>
                    <P2GText variant="subtitle1" fontWeight="bold">
                      Will the contents be the same for all {count} parcels?
                    </P2GText>
                  </P2GBox>
                  <P2GGrid container gap={1} mt={2} wrap="nowrap">
                    <P2GGrid item xs={6}>
                      <P2GOptionContainer
                        variant="multi-address"
                        selected={isSameContents === true}
                        onClick={() => setIsSameContents(true)}
                      >
                        <P2GRadio
                          checked={isSameContents === true}
                          required
                          name="content_only"
                          label={<P2GText>Yes</P2GText>}
                          dataTestId="content_only"
                        />
                      </P2GOptionContainer>
                    </P2GGrid>
                    <P2GGrid item xs={6}>
                      <P2GOptionContainer
                        variant="multi-address"
                        selected={isSameContents === false}
                        onClick={() => setIsSameContents(false)}
                      >
                        <P2GRadio
                          checked={isSameContents === false}
                          required
                          name="content_only"
                          label={<P2GText>No</P2GText>}
                          dataTestId="content_only"
                        />
                      </P2GOptionContainer>
                    </P2GGrid>
                  </P2GGrid>
                  <P2GBox mt={4} textAlign="right">
                    <P2GButton
                      fullWidth
                      size="large"
                      disabled={isSameContents === undefined}
                      variant="primary"
                      onClick={onYesClick}
                    >
                      Confirm
                    </P2GButton>
                  </P2GBox>
                </P2GBox>
              </P2GGrid>
            </P2GGrid>
          </>
        }
      />
      {isSendToMultiple ? (
        <P2GLink onClick={revert} variant="body1">
          {t('SEND_TO_MULTIPLE_REVERT', { defaultValue: 'Send to Same Address' })}
        </P2GLink>
      ) : (
        <P2GLink onClick={onClick} variant="body1">
          {t('SEND_TO_MULTIPLE', { defaultValue: 'Send to Multiple Addresses' })}
        </P2GLink>
      )}
    </>
  );
}
